import { useCallback, useEffect, useRef, useState } from 'react';

import type { TutorAIPromptConfigViewModel } from '@/shared/api/generated/types/TutorAIPromptConfigViewModel';
import type { TutorAISetPromptConfigViewModel } from '@/shared/api/generated/types/TutorAISetPromptConfigViewModel';

import type { TutorPromptScope } from '../types/tutorAi';

/**
 * Callbacks opcionais de conclusão do `save`.
 *
 * Deixam a UI reagir ao resultado da mutation (ex.: toast de sucesso ou de erro) sem a
 * mutation em si sair do hook — quem chama `save` decide o que fazer com o resultado.
 */
export interface TutorPromptSaveCallbacks {
  readonly onSuccess?: () => void;
  readonly onError?: () => void;
}

/** Estado de um campo de prompt: valor persistido, rascunho e ações. */
export interface TutorPromptFieldState {
  /** Conteúdo persistido no servidor. String vazia quando herda do global. */
  readonly content: string;
  readonly draft: string;
  readonly isDirty: boolean;
  /** Verdadeiro quando a API devolveu `content: null` — nenhum prompt próprio configurado. */
  readonly isInherited: boolean;
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  /** Verdadeiro quando o GET do prompt falhou — a tela não pode afirmar herança nesse caso. */
  readonly isError: boolean;
  readonly setDraft: (value: string) => void;
  /** Salva o rascunho. Ver {@link TutorPromptSaveCallbacks}. */
  readonly save: (callbacks?: TutorPromptSaveCallbacks) => void;
  readonly discard: () => void;
  /** Repete o GET do prompt — usado pelo retry do estado de erro. */
  readonly refetch: () => void;
}

/** Contrato mínimo da query gerada que este hook consome. */
interface PromptQueryLike {
  readonly data: TutorAIPromptConfigViewModel | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => unknown;
}

/** Contrato mínimo da mutation gerada que este hook consome. */
interface PromptMutationLike {
  readonly mutate: (
    variables: { data: TutorAISetPromptConfigViewModel },
    options?: TutorPromptSaveCallbacks,
  ) => void;
  readonly isPending: boolean;
}

/**
 * Converte o escopo da UI nos dois campos que a API espera.
 *
 * `classStudentId` é **omitido** no escopo global em vez de ir como `null`: o contrato
 * aceita os dois, e omitir deixa o payload dizendo uma coisa só.
 */
function buildScopePayload(scope: TutorPromptScope) {
  return scope.kind === 'global'
    ? { isGlobal: true }
    : { isGlobal: false, classStudentId: scope.classStudentId };
}

/**
 * Lógica de rascunho compartilhada pelos campos de persona e memória.
 *
 * Persona e memória são pares GET/PUT distintos, então cada um tem seu hook público; o
 * comportamento de rascunho é idêntico e vive aqui para os dois não divergirem.
 *
 * O rascunho ressincroniza quando o conteúdo persistido ou o escopo muda, mas **não**
 * quando o operador já digitou algo além do que foi visto pela última vez do servidor —
 * do contrário, texto digitado depois de clicar Salvar seria apagado quando o PUT resolve,
 * invalida a query e o refetch chega com o novo `content` (a janela é a latência do PUT).
 * A dependência do escopo continua necessária: dois escopos sem prompt têm o mesmo
 * `content` (`''`), então sem ela o texto digitado numa turma vazaria para outra ao navegar
 * sem salvar — por isso a troca de escopo sempre adota o conteúdo do servidor, mesmo com
 * rascunho divergente.
 *
 * @param query - Query gerada do GET do prompt
 * @param mutation - Mutation gerada do PUT do prompt
 * @param scope - Escopo global ou de turma
 * @returns Estado do campo e ações de salvar/descartar
 */
export function usePromptDraft(
  query: PromptQueryLike,
  mutation: PromptMutationLike,
  scope: TutorPromptScope,
): TutorPromptFieldState {
  const rawContent = query.data?.content;
  const content = rawContent ?? '';
  // Um GET que falhou também devolve `content` undefined — isso não é herança, é falta de
  // informação. Só a API que respondeu diz se o escopo herda do global.
  const isInherited = !query.isError && (rawContent === null || rawContent === undefined);

  const [draft, setDraft] = useState(content);
  const scopeKey = scope.kind === 'global' ? 'global' : `class:${scope.classStudentId}`;

  const lastSyncedContentRef = useRef(content);
  const lastSyncedScopeKeyRef = useRef(scopeKey);

  useEffect(() => {
    // Captura o valor sincronizado anterior ANTES de atualizar os refs: o updater do
    // setDraft abaixo só é executado por React na reconciliação seguinte, então ler o ref
    // ali dentro (em vez desta cópia local) compararia o rascunho contra o `content` NOVO,
    // não contra o que foi visto da última vez — a ressincronização nunca bateria.
    const previousSyncedContent = lastSyncedContentRef.current;
    const scopeChanged = lastSyncedScopeKeyRef.current !== scopeKey;

    lastSyncedContentRef.current = content;
    lastSyncedScopeKeyRef.current = scopeKey;

    setDraft(previousDraft => {
      const draftMatchesLastSync = previousDraft === previousSyncedContent;
      return scopeChanged || draftMatchesLastSync ? content : previousDraft;
    });
  }, [content, scopeKey]);

  const save = useCallback(
    (callbacks?: TutorPromptSaveCallbacks) => {
      mutation.mutate({ data: { content: draft, ...buildScopePayload(scope) } }, callbacks);
    },
    [mutation, draft, scope],
  );

  const discard = useCallback(() => setDraft(content), [content]);

  const refetch = useCallback(() => {
    void query.refetch();
  }, [query]);

  return {
    content,
    draft,
    isDirty: draft !== content,
    isInherited,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    isError: query.isError,
    setDraft,
    save,
    discard,
    refetch,
  };
}
